import {
  Box,
  Button,
  Modal
} from '@platform/ui-library';
import {
  FC,
  useEffect,
  useState
} from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import { Input, UploadProgress } from '@app/form_components';
import { useDesign, useServices } from '@app/hooks';
import {
  BehaviorSubject,
  LoadingProgress,
  LoadingProgressStatus,
  LoadingProgressStatusPhase
} from '@app/types';
import { SaveToRepository } from '@app/utilities/SaveToRepository';
import { BranchManager } from '@app/utilities/versioning/BranchManager';
import styles from './BranchDialog.module.scss';

const validBranchSearch = /^[a-zA-Z][a-zA-Z0-9_-]{2,48}[a-zA-Z0-9]$/;

type BranchDialogProps = {
  closeAction: () => void;
  isHotfix: boolean;
  isOpen: boolean;
};

/** Dialog for creating new branches off from the main branch. */
export const BranchDialog: FC<BranchDialogProps> = ({
  closeAction,
  isHotfix,
  isOpen
}) => {
  const {
    avsService,
    errorMessageService,
    globalModalsService,
    loadingService
  } = useServices();
  const serialized = useDesign();
  const [branchName, setBranchName] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [branchProgress, setBranchProgress] = useState<LoadingProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mainProgress, setMainProgress] = useState<LoadingProgress | null>(null);
  const { designId } = serialized;

  useEffect(() => {
    if (!isOpen) {
      setBranchName('');
      setBranchProgress(null);
      setErrorMessage('');
      setMainProgress(null);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const result = await avsService.getBranches(designId);
        if (isMounted) {
          setBranches(result.map(({ name }) => name));
        }
      } catch (error) {
        errorMessageService.addError(error as Error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [avsService, designId, errorMessageService, isOpen]);

  if (!isOpen) {
    return null;
  }

  const transformName = (raw: string) => {
    return raw.trim().replace(/\s+/g, '_').replace(/[-_]+$/g, '');
  };

  const getError = (name: string) => {
    if (!name) {
      return 'Cannot save empty branch name';
    } else if (!validBranchSearch.test(name)) {
      return window.l.form.invalid_branch_name;
    } else if (branches.includes(name)) {
      return 'Branch name already exists';
    }
    return '';
  };

  const createBranch = async () => {
    const name = transformName(branchName);
    const message = getError(name);
    setBranchName(name);
    setErrorMessage(message);
    if (message) {
      return;
    }

    setIsLoading(true);

    try {
      const {
        mainBranch,
        mainBranchChangeLogMap,
        featureBranch,
        featureBranchChangeLogMap
      } = await loadingService.useLoading(
        () => {
          const branchManager = new BranchManager();
          return branchManager.initFeatureBranch(
            serialized,
            name,
            DesignerSetting.s.author,
            isHotfix
          );
        },
        `Creating branch ${name} ...`
      );

      const mainProgressMonitor = new BehaviorSubject<LoadingProgressStatus>({
        phase: LoadingProgressStatusPhase.preparing
      });
      const branchProgressMonitor = new BehaviorSubject<LoadingProgressStatus>({
        phase: LoadingProgressStatusPhase.preparing
      });
      setMainProgress((!mainBranch) ? null : {
        label: 'Main Branch',
        startedAt: Date.now(),
        subject: mainProgressMonitor
      });
      setBranchProgress({
        label: name,
        startedAt: Date.now(),
        subject: branchProgressMonitor
      });

      const { clientName } = featureBranch;
      await avsService.createBranch(
        featureBranch.designId,
        {
          d2Client: clientName,
          d2Design: `${clientName}${featureBranch.branchName}`,
          id: featureBranch.branchId,
          name: featureBranch.branchName
        }
      );

      const saveMain = (!mainBranch) ? Promise.resolve(serialized) : (
        SaveToRepository.saveToRepoDesign(
          mainBranch,
          mainBranchChangeLogMap,
          mainProgressMonitor
        )
      );

      const saveBranch = SaveToRepository.saveToRepoDesign(
        featureBranch,
        featureBranchChangeLogMap,
        branchProgressMonitor
      );

      const [serializedMain, serializedBranch] = await Promise.all([saveMain, saveBranch]);
      if (serializedMain == null || serializedBranch == null) {
        return;
      }

      window.apm.refreshApplication(serializedMain);

      const switchBranch = await globalModalsService.openConfirmationModal({
        children: (
          <div className="box content">
            <p>Branch <strong>{serializedBranch.branchName}</strong> successfully created.</p>
            <p>You can switch to the new branch or continue with the current application.</p>
          </div>
        ),
        labels: {
          cancelButton: 'Continue',
          continueButton: 'Switch to branch',
          header: 'Branch created'
        }
      });

      if (switchBranch) {
        serializedBranch.lastUpdate = window.apm.timestamp;
        await loadingService.useLoading(
          () => window.colm.importDocument(serializedBranch),
          `Opening branch ${name}...`
        );
        const url = new URL(window.location.href);
        const search_params = new URLSearchParams(url.search);
        search_params.set('g', serializedBranch.branchId);
        url.search = search_params.toString();
        window.location.replace(url.toString().split('#')[0]);
      }
      closeAction();
    } catch (error) {
      console.error(error);
      await errorMessageService.addError(error as Error);
    } finally {
      setBranchProgress(null);
      setIsLoading(false);
      setMainProgress(null);
    }
  };

  return (
    <Modal className="ui-box-small" isOpen={isOpen}>
      <Box theme="noPadding">
        <div className="box header">
          <h2>Current application</h2>
        </div>
        <div className="box content">
          <table>
            <tbody>
              <tr>
                <th>Client</th>
                <td>{serialized.clientName}</td>
              </tr>
              <tr>
                <th>Application</th>
                <td>{serialized.designName}</td>
              </tr>
              <tr>
                <th>Version</th>
                <td>{serialized.version}</td>
              </tr>
            </tbody>
          </table>
          <Input
            disabled={isLoading}
            error={errorMessage}
            label="Branch Name"
            name="branchName"
            onBlur={() => {
              const transformedName = transformName(branchName);
              setBranchName(transformName);
              setErrorMessage(getError(transformedName));
            }}
            onChange={setBranchName}
            value={branchName}
          />
          {(!mainProgress) ? null : (
            <UploadProgress classNames={{ container: styles.progressContainer }} {...mainProgress} />
          )}
          {(!branchProgress) ? null : (
            <UploadProgress classNames={{ container: styles.progressContainer }} {...branchProgress} />
          )}
        </div>
        <div className="box footer">
          <Button disabled={isLoading} onClick={closeAction} theme="outline">
            {window.l.popup.close}
          </Button>
          <Button disabled={isLoading} onClick={createBranch}>
            Create Branch
          </Button>
        </div>
      </Box>
    </Modal>
  );
};
